import React from "react";

const MethodologyArchitecture: React.FC = () => (
  <div className="space-y-6 text-sm md:text-base leading-relaxed text-[#380C1E]/90 font-light">
    <section>
      <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
        4. Instrument architecture
      </h2>

      <h3 className="font-serif text-xl font-normal text-[#380C1E] pt-4 pb-2">
        4.1 Why forced choice
      </h3>
      <p className="mb-3">
        Earlier versions of the quiz (versions 2 and 3) used agreement rating
        scales. In review they showed a predictable problem: a deeply exhausted
        respondent can endorse nearly everything at the high end, which yields a
        flat profile with every domain scored high and no usable signal about
        which domain was primary. This is a known behavior of Likert-type scales
        under acquiescence and extreme-response styles, and it is the standard
        motivation for multidimensional forced-choice formats, in which the
        respondent must choose between statements rather than rate each one
        (Brown and Maydeu-Olivares, 2011; Hontangas et al., 2015).
      </p>
      <p>
        Forced choice trades one thing for another. It always differentiates
        within a person (the respondent cannot pick both), which is exactly the
        property the instrument needs for attribution. The cost is that the
        resulting scores are ipsative: they describe the relative standing of
        the six domains within one respondent and carry no information about how
        that respondent compares to anyone else (Brown and Maydeu-Olivares,
        2013). Section 10 returns to this.
      </p>

      <h3 className="font-serif text-xl font-normal text-[#380C1E] pt-4 pb-2">
        4.2 Pair construction
      </h3>
      <p className="mb-3">
        The six domains form 15 unique pairings. Each pairing appears exactly
        twice in the instrument, once under each of two stems, with different
        wording each time. This yields 30 choice screens. Each domain therefore
        appears in exactly 10 screens, and each domain's raw score has a fixed
        range of 0 to 10. The two appearances of each pairing are what allow the
        convergence check in Section 5.5.
      </p>
      <p className="mb-3">
        Screen order was constrained so that no domain appears on two
        consecutive screens, to reduce carry-over and pattern-recognition
        effects. The domain behind each statement is never shown during the
        quiz; the six labels are revealed only in the report.
      </p>
      <p>
        Each pair was written to a weight-matching rule: the two statements in a
        pair should be roughly equally comfortable, or equally uncomfortable, to
        admit. A pair in which one option is obviously more embarrassing would
        push respondents toward the safer option regardless of accuracy, which
        is the social-desirability problem forced choice is meant to reduce, not
        reintroduce.
      </p>

      <h3 className="font-serif text-xl font-normal text-[#380C1E] pt-4 pb-2">
        4.3 The two stems
      </h3>
      <p className="mb-3">
        The 30 screens are split into two halves of 15, each under a different
        stem.
      </p>
      <p className="mb-3">
        Stem One (screens 1 to 15): "Lately, this is more like me..." Each
        statement describes a lived symptom. Sample pairs:
      </p>
      <ul className="list-disc pl-6 space-y-1 mb-3">
        <li>
          "I keep replaying the same story about why things can't change for
          me." (Beliefs) / "I'm tired in a way that sleep doesn't fix." (Body)
        </li>
        <li>
          "I say yes while everything in me is saying no." (Boundaries) / "My
          days are one loop: work, home, feed everybody, repeat." (Business)
        </li>
        <li>
          "I avoid looking at my bank statements because I already know."
          (Budget) / "Everybody's meals, health, and appointments come before
          mine." (Body)
        </li>
      </ul>
      <p className="mb-3">
        Stem Two (screens 16 to 30): "Right now, this would help me more..."
        Each statement describes a desired relief. Sample pairs:
      </p>
      <ul className="list-disc pl-6 space-y-1 mb-3">
        <li>
          "Permission to say no without feeling guilty." (Boundaries) / "One
          person who really sees me." (Belonging)
        </li>
        <li>
          "Waking up and actually feeling rested." (Body) / "Work that feels
          connected to my purpose again." (Business)
        </li>
        <li>
          "A plan for my money instead of a monthly scramble." (Budget) / "Fewer
          plates to spin, without feeling like I let everybody down."
          (Boundaries)
        </li>
      </ul>
      <p className="mb-3">
        The two stems are the instrument's central design idea. Asking about
        symptoms alone invites the respondent to report whatever is most
        socially acceptable to be tired from. Asking about desired relief
        approaches the same domain from the other side: what a person most wants
        fixed is often a more honest signal of what is most broken than what she
        is willing to say is wrong. Where the two stems agree, the attribution
        is stronger; where they disagree, the report says so rather than hiding
        it.
      </p>
      <p>
        The domain labels shown in parentheses above are scoring metadata. The
        complete 30-pair item bank is not reproduced in this document; the
        instrument's questions, scoring, and result types are the intellectual
        property of the author, and full publication would also allow
        respondents to reverse-engineer their results, which would degrade the
        very signal the instrument exists to capture. Qualified researchers may
        request the full item bank for review purposes through the contact on
        the site.
      </p>

      <h3 className="font-serif text-xl font-normal text-[#380C1E] pt-4 pb-2">
        4.4 The intensity block
      </h3>
      <p className="mb-3">
        Three additional screens follow the 30 pairs. They look different on
        purpose (a single question with stacked answer options, no stem) because
        they measure something different: the 30 pairs estimate where the
        burnout comes from, and these three estimate how deep it goes.
      </p>
      <p className="mb-3">
        Screen 31, duration: "How long have you felt this way?" Four options
        scored 1 to 4, from "A few weeks. This feels like a season" to "So long
        I can't remember feeling any other way."
      </p>
      <p className="mb-3">
        Screen 32, impact: "How much is it shaping your day-to-day right now?"
        Four options scored 1 to 4, from "It comes and goes. I have good weeks"
        to "I don't feel like myself at all anymore."
      </p>
      <p>
        Screen 33, presentation: "Where does it show up the loudest?" Three
        options (body, emotions, spirit). This item is not scored. It selects
        which of three short passages appears in the report and has no effect on
        any number.
      </p>

      <h3 className="font-serif text-xl font-normal text-[#380C1E] pt-4 pb-2">
        4.5 Administration
      </h3>
      <p className="mb-3">
        The quiz is mobile-first, one screen at a time, one tap per screen, with
        no typing. It takes roughly three to four minutes. The instruction
        screen tells the respondent: "Sometimes both will sound like you.
        Sometimes neither will. That's okay: pick the one that's CLOSER to your
        life lately, most of the time. Go with your first gut answer." This
        instruction is part of the instrument; it is the release valve that
        makes forced choice tolerable when neither option fits well, and it sets
        the reference period (lately, most of the time) that all items share.
      </p>
      <p>
        A respondent may go back and change an answer. When she does, the
        earlier pick is removed and the new one recorded, so that the 30 screens
        always contribute exactly 30 points in total. No name or email is
        requested before or during the questions, and the primary result is
        shown before any contact information is collected.
      </p>
    </section>
  </div>
);

export default MethodologyArchitecture;
